import { type CSSProperties, useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { HotKeyLineWithButtonAndDescription } from '@/components/HotKeyLineWithButtonAndDescription.tsx';
import { Key } from '@/hooks/shortcutHooks/hotkeys/KeyStrokes.ts';
import { type Hotkey, InventoryHotkeyNames } from '@/hooks/shortcutHooks/hotkeys/types.ts';
import { DefaultInventoryHotKeys } from '@/hooks/shortcutHooks/useInventoryHotkeys.ts';
import { responsive } from '@/utils';

const DEFAULT_STYLE: CSSProperties = {
  width: '32rem',
  height: 'fit-content',
};

type InventoryShortcutsProps = {
  style?: CSSProperties;
};

export const InventoryShortcuts = ({ style = DEFAULT_STYLE }: InventoryShortcutsProps) => {
  const { t } = useTranslation();
  const inventoryHotkeys = useMemo<Hotkey[]>(
    () => [
      {
        ...DefaultInventoryHotKeys[InventoryHotkeyNames.FocusSearchKey],
        description: t('shortcuts.inventory.focusSearch'),
      },
      {
        ...DefaultInventoryHotKeys[InventoryHotkeyNames.RestoreFocusKey],
        description: t('shortcuts.inventory.restoreFocus'),
      },
      {
        ...DefaultInventoryHotKeys[InventoryHotkeyNames.ToggleFlatListKey],
        description: t('shortcuts.inventory.toggleFlatList'),
      },
      {
        ...DefaultInventoryHotKeys[InventoryHotkeyNames.ToggleHideLockedKey],
        description: t('shortcuts.inventory.toggleHideLocked'),
      },
      {
        primary: Key.arrow.ArrowUp,
        description: t('shortcuts.inventory.previousItem'),
      },
      {
        primary: Key.arrow.ArrowDown,
        description: t('shortcuts.inventory.nextItem'),
      },
      {
        primary: Key.arrow.ArrowLeft,
        description: t('shortcuts.inventory.closeOrParent'),
      },
      {
        primary: Key.arrow.ArrowRight,
        description: t('shortcuts.inventory.openOrChild'),
      },
      {
        primary: Key.KeyE,
        hotkeyModifier: { modifier: 'Shift' },
        description: t('shortcuts.inventory.editItem'),
      },
      {
        primary: Key.navigation.Enter,
        hotkeyModifier: { modifier: 'Shift' },
        description: t('shortcuts.inventory.renameItem'),
      },
    ],
    [t]
  );

  return (
    <FlexAligner
      direction='column'
      alignment='center'
      style={style}
      gap={responsive.spacing['3xlarge']}
    >
      {inventoryHotkeys.map(hotkey => (
        <HotKeyLineWithButtonAndDescription
          key={`${hotkey.hotkeyModifier?.modifier ?? ''}-${hotkey.primary.code}`}
          {...hotkey}
        />
      ))}
    </FlexAligner>
  );
};
